import type { EmployeeField, PeriodType } from "#shared/segmentsTypes.ts";
import { Button } from "@/components/ui/button.tsx";
import { FieldError } from "@/components/ui/field.tsx";
import { Select, SelectContent, SelectItem, SelectLabel, SelectTrigger } from "@/components/ui/select.tsx";
import { LockClosedIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { useI18nextInstance } from "@squide/i18next";
import { useCallback, useMemo } from "react";
import type { Key } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { segmentsI18NextInstanceKey } from "../../i18n/i18next.ts";
import { RuleValueEditor } from "./RuleValueEditor.tsx";
import { CONDITIONS_BY_FIELD_TYPE, type RuleDraft, validateRuleDraft, valueArity } from "./ruleConditions.ts";

export interface SegmentRuleRowProps {
    draft: RuleDraft;
    fields: EmployeeField[];
    isReadOnly?: boolean;
    shouldShowErrors?: boolean;
    canRemove: boolean;
    isLocked?: boolean;
    onChange: (draft: RuleDraft) => void;
    onRemove: (key: string) => void;
}

export function SegmentRuleRow({ draft, fields, isReadOnly, shouldShowErrors, canRemove, isLocked, onChange, onRemove }: SegmentRuleRowProps) {
    const i18nextInstance = useI18nextInstance(segmentsI18NextInstanceKey);
    const { t } = useTranslation("segmentRules", { i18n: i18nextInstance });

    const field = fields.find(candidate => candidate.id === draft.fieldId);
    const conditions = useMemo(() => (field ? CONDITIONS_BY_FIELD_TYPE[field.type] : []), [field]);

    const handleFieldChange = useCallback(
        (key: Key | null) => {
            const nextField = fields.find(candidate => candidate.id === key);

            onChange({ ...draft, fieldId: nextField?.id, condition: undefined, values: [], periodType: undefined, hasUnsupportedCondition: false });
        },
        [draft, fields, onChange]
    );

    const handleConditionChange = useCallback(
        (key: Key | null) => {
            const nextCondition = conditions.find(candidate => candidate === key);
            if (!nextCondition) {
                return;
            }

            const arity = valueArity(nextCondition);
            const values = arity === "range" || arity === "periodRange" ? draft.values.slice(0, 2) : arity === "multiple" ? draft.values : draft.values.slice(0, 1);
            const isPeriod = arity === "period" || arity === "periodRange";

            onChange({ ...draft, condition: nextCondition, values, periodType: isPeriod ? draft.periodType : undefined, hasUnsupportedCondition: false });
        },
        [conditions, draft, onChange]
    );

    const handleValuesChange = useCallback((values: string[]) => onChange({ ...draft, values }), [draft, onChange]);
    const handlePeriodTypeChange = useCallback((periodType: PeriodType) => onChange({ ...draft, periodType }), [draft, onChange]);
    const handleRemove = useCallback(() => onRemove(draft.key), [draft.key, onRemove]);

    const fieldItems = useMemo(() => fields.map(entry => ({ id: entry.id, name: entry.displayName })), [fields]);

    const error = shouldShowErrors ? validateRuleDraft(draft) : undefined;
    const propertyError = draft.fieldId && !field ? t("property.unavailable") : error && !draft.fieldId ? t("incomplete") : undefined;
    const conditionError = draft.hasUnsupportedCondition ? t("condition.unsupported") : error && draft.fieldId && !draft.condition ? t("incomplete") : undefined;
    const valueError = error && draft.condition ? t(error === "missingRangeEnd" ? "missingRangeEnd" : "incomplete") : undefined;

    return (
        <li className="flex flex-col gap-1">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                <Select name="property" aria-label={t("columns.property")} placeholder={t("property.placeholder")} selectedKey={draft.fieldId ?? null} onSelectionChange={handleFieldChange} isDisabled={isReadOnly} isInvalid={!!propertyError} className="min-w-0 sm:w-48">
                    <SelectTrigger />
                    <SelectContent items={fieldItems}>
                        {item => (
                            <SelectItem id={item.id} textValue={item.name}>
                                <SelectLabel>{item.name}</SelectLabel>
                            </SelectItem>
                        )}
                    </SelectContent>
                    <FieldError>{propertyError}</FieldError>
                </Select>

                <Select name="condition" aria-label={t("columns.condition")} placeholder={t("condition.placeholder")} selectedKey={draft.condition ?? null} onSelectionChange={handleConditionChange} isDisabled={isReadOnly || !field} isInvalid={!!conditionError} className="min-w-0 sm:w-56">
                    <SelectTrigger />
                    <SelectContent items={conditions.map(condition => ({ id: condition }))}>
                        {item => (
                            <SelectItem id={item.id} textValue={t(`conditions.${item.id}`)}>
                                <SelectLabel>{t(`conditions.${item.id}`)}</SelectLabel>
                            </SelectItem>
                        )}
                    </SelectContent>
                    <FieldError>{conditionError}</FieldError>
                </Select>

                <div className="flex min-w-0 flex-col gap-1 sm:flex-1">
                    {field && draft.condition ? <RuleValueEditor field={field} condition={draft.condition} values={draft.values} periodType={draft.periodType} isDisabled={isReadOnly} error={valueError} onChange={handleValuesChange} onPeriodTypeChange={handlePeriodTypeChange} /> : null}
                </div>

                {canRemove ? (
                    <Button type="button" intent="plain" size="sq-md" aria-label={t("removeRule")} onPress={handleRemove}>
                        <XMarkIcon aria-hidden="true" />
                    </Button>
                ) : (
                    isLocked && (
                        <div className="flex size-9 shrink-0 items-center justify-center" aria-hidden="true">
                            <LockClosedIcon className="text-muted-fg size-4" />
                        </div>
                    )
                )}
            </div>
        </li>
    );
}
